import { useMutation } from '@tanstack/react-query';
import {
  FlightBriefingParams,
  PacketDownloadFailure,
  PacketDownloadResult,
  PacketTypeOption,
} from '../../@types/FlightBriefing.type';
import { getFlightBriefingDocument } from '../../service/flightBriefing/apiService';
import {
  buildDocumentFileName,
  buildZipFileName,
  createZip,
  saveBlob,
  withMimeType,
} from '../../utils/fileDownload';

interface DownloadPacketVariables {
  params: FlightBriefingParams;
  packetTypes: PacketTypeOption[];
  accessToken: string;
}

const errorMessage = (error: unknown) =>
  error instanceof Error && error.message
    ? error.message
    : 'An unexpected error occurred.';

/**
 * Requests every selected packet type in parallel.
 * - One packet type: the document is downloaded as-is.
 * - Several packet types: the successful documents are packaged into one ZIP.
 * Failures are reported per packet type and never discard successful documents.
 * Nothing is downloaded when every request fails.
 */
export const downloadPackets = async ({
  params,
  packetTypes,
  accessToken,
}: DownloadPacketVariables): Promise<PacketDownloadResult> => {
  const results = await Promise.allSettled(
    packetTypes.map((packetType) =>
      getFlightBriefingDocument(packetType.value, params, accessToken),
    ),
  );

  const documents: {
    packetType: PacketTypeOption;
    fileName: string;
    blob: Blob;
  }[] = [];
  const failures: PacketDownloadFailure[] = [];

  results.forEach((result, index) => {
    const packetType = packetTypes[index];
    if (result.status === 'fulfilled') {
      documents.push({
        packetType,
        fileName: buildDocumentFileName(params, packetType),
        blob: withMimeType(result.value, packetType.format),
      });
    } else {
      failures.push({ packetType, message: errorMessage(result.reason) });
    }
  });

  if (documents.length === 0) {
    return { downloaded: [], failures };
  }

  try {
    let fileName: string;
    if (packetTypes.length === 1) {
      fileName = documents[0].fileName;
      saveBlob(documents[0].blob, fileName);
    } else {
      fileName = buildZipFileName(params);
      saveBlob(await createZip(documents), fileName);
    }
    return {
      downloaded: documents.map((doc) => doc.packetType),
      fileName,
      failures,
    };
  } catch (error) {
    const message = `Unable to save the file: ${errorMessage(error)}`;
    return {
      downloaded: [],
      failures: [
        ...documents.map((doc) => ({ packetType: doc.packetType, message })),
        ...failures,
      ],
    };
  }
};

const useDownloadPacket = () =>
  useMutation({
    mutationFn: downloadPackets,
    retry: false,
  });

export default useDownloadPacket;
